<template>
  <ChartLayout :title="title" :half="half">
    <template #params>
      <ControlGroup
        ><Toggle v-model="log" label="Log scale" small
      /></ControlGroup>
    </template>
    <DataGetter
      :url="apiRoute"
      :params="{ startYear, endYear, topic: 'Patents', indicator: ['Total'] }"
      v-slot="{ data }"
      lazy
    >
      <DataSlicer
        :data="data"
        :format-data="formatData"
        x-axis-dimension="country"
        y-axis-dimension="techSector"
        value-dimension="value"
        no-single-value
        class="exit-chart"
        v-slot="{ slicedData }"
      >
        <footer class="m-chart__footer">
          <ChartLegend
            :legend-items="[
              { name: 'Fossil fuel patents', colorIndex: 5 },
              { name: 'Clean energy patents', colorIndex: 1 },
            ]"
          />
        </footer>
        <Chart
          :options="{
            ...chartOptions,
            series: formatSeries(slicedData),
          }"
        />
      </DataSlicer>
    </DataGetter>
    <template #notes>
      <p v-if="endYear >= provisionalYear">The datapoints for 2022 and 2023 are considered provisional due to the time lag in filing applications in the PATSTAT database.</p>
      </template>
  </ChartLayout>
</template>

<script>
import { Chart } from "highcharts-vue";
import ChartOptions, {
  ChartLayout,
  ChartLegend,
} from "@international-energy-agency/vue-ui/highcharts";
import {
  DataGetter,
  DataSlicer,
  ControlGroup,
  Toggle,
} from "@international-energy-agency/vue-ui";
export default {
  props: {
    title: {
      type: String,
      required: false,
    },
    apiRoute: {
      type: String,
      default: "https://api.iea.org",
    },
    startYear: {
      type: Number,
      required: true,
    },
    endYear: {
      type: Number,
      required: true,
    },
    provisionalYear: {
      type: Number,
      default: 2022
    },
    half: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    Chart,
    ChartLayout,
    ChartLegend,
    DataGetter,
    DataSlicer,
    ControlGroup,
    Toggle,
  },
  data() {
    return {
      log: true,
    };
  },
  computed: {
    chartOptions() {
      const { log } = this;
      return ChartOptions.buildOptions(
        {
          chart: {
            inverted: true,
            height: 1200,
          },
          xAxis: {
            type: "category",
          },
          yAxis: {
            opposite: true,
            type: log ? "logarithmic" : "normal",
            min: log ? 1 : null,
            max: log ? 400000 : null,
            title: {
              text: "Number of patents",
            },
            className: "highcharts-show-grid",
          },
          tooltip: {
            formatter: function () {
              return `<span class="highcharts-header">${this.point.name}, ${
                this.point.series.name
              }</span><br>
              <span class="highcharts-color-5">●</span> Fossil fuel: <b>${this.point.low.toFixed(
                0
              )}</b><br>
              <span class="highcharts-color-1">●</span> Clean energy: <b>${this.point.high.toFixed(
                0
              )}</b>`;
            },
          },
          plotOptions: {
            dumbbell: {
              marker: {
                enabled: true,
              },
              lineWidth: 4,
            },
          },
        },
        "dumbbell"
      );
    },
  },
  methods: {
    formatData(data) {
      const cleanEnergy = data
        .filter((row) => row.techSector === "Clean energy")
        .map((row) => ({ ...row, techSector: "Clean energy patents" }));
      const fossilFuel = data.filter(
        (row) => row.techSector !== "Clean energy"
      );
      const fossilFuelTotal = fossilFuel.reduce((total, row) => {
        const existing = total.find((r) => r.country === row.country);
        if (existing) {
          existing.value += row.value;
          return total;
        }
        return [...total, { ...row, techSector: "Fossil fuel patents" }];
      }, []);
      return [...fossilFuelTotal, ...cleanEnergy];
    },
    formatSeries(seriesData) {
      const groupingSeries = seriesData.find(
        (series) => series.name === "Fossil fuel patents"
      );
      const oppositeSeries = seriesData.find(
        (series) => series.name === "Clean energy patents"
      );
      const data = groupingSeries.data
        .filter(
          (point) =>
            point[0] &&
            point[1] &&
            oppositeSeries.data.findIndex((p) => p[0] === point[0] && p[1]) > -1
        )
        .map((point) => {
          const low = point[1];
          const high = oppositeSeries.data.find((p) => p[0] === point[0])[1];
          return {
            name: point[0],
            low,
            high,
          };
        });
      // .sort((a,b) => (a.low < a.high ? a.low : a.high) - (b.low < b.high ? b.low : b.high));
      // Split into two series to get around class bug with lollipop-stem
      return [
        {
          name: "Number of patents",
          showInLegend: false,
          colorIndex: 1,
          data: data
            .filter((point) => point.low <= point.high)
            .sort((a, b) => a.high - b.high),
        },
        {
          name: "Number of patents",
          showInLegend: false,
          colorIndex: 5,
          data: data
            .filter((point) => point.low > point.high)
            .sort((a, b) => a.high - b.high),
        },
      ];
    },
  },
};
</script>

<style>
.exit-chart .highcharts-lollipop-low {
  fill: #ffb743 !important;
}
.exit-chart .highcharts-lollipop-high {
  fill: #3e7ad3 !important;
}
</style>
